import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  X, ChevronRight, ChevronLeft, Sparkles, FileText,
  Users, Briefcase, Wand2, BookOpen, Play, CheckCircle,
  ArrowRight, Rocket
} from 'lucide-react';

interface OnboardingTourProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

interface TourStep {
  id: string;
  title: string;
  description: string;
  icon: any;
  iconColor: string;
  targetPath?: string;
  targetSelector?: string;
  position?: 'center' | 'top' | 'bottom' | 'left' | 'right';
  action?: {
    label: string;
    onClick: () => void;
  };
  tips?: string[];
}

const TOUR_STEPS: TourStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to Lawity Platform',
    description: 'Let\'s take a quick tour to help you get started. This platform helps you create, manage, and automate legal documents with powerful AI assistance.',
    icon: Rocket,
    iconColor: 'from-violet-500 to-purple-600',
    position: 'center',
    tips: [
      'You can skip this tour anytime and access it later from Help',
      'The tour will guide you through the main features',
    ],
  },
  {
    id: 'templates',
    title: 'Template Studio',
    description: 'Create powerful document templates using our DSL (Domain Specific Language). Templates support variables, conditionals, loops, and reusable clauses.',
    icon: FileText,
    iconColor: 'from-blue-500 to-cyan-600',
    targetPath: '/templates',
    tips: [
      'Use {{variable}} syntax to insert dynamic content',
      'Press ⌘+K to quickly search and navigate',
      'Templates auto-save as you work',
    ],
  },
  {
    id: 'ai-features',
    title: 'AI-Powered Features',
    description: 'Let AI help you create templates, analyze documents, suggest clauses, and extract data. Our Claude-powered assistant understands legal documents.',
    icon: Wand2,
    iconColor: 'from-amber-500 to-orange-600',
    position: 'center',
    tips: [
      'Click the floating AI button (bottom-right) for quick access',
      'Use "AI Generate" to create templates from descriptions',
      'AI can analyze documents for risks and key terms',
    ],
  },
  {
    id: 'clauses',
    title: 'Clause Library',
    description: 'Build a library of reusable clauses with jurisdiction variants. Insert them into any template with a simple reference.',
    icon: BookOpen,
    iconColor: 'from-green-500 to-emerald-600',
    targetPath: '/clauses',
    tips: [
      'Create clauses once, reuse everywhere',
      'Add jurisdiction-specific variants (WI, FL, etc.)',
      'Use {{@clause:clause_id}} to insert clauses',
    ],
  },
  {
    id: 'clients',
    title: 'Client Management',
    description: 'Manage your clients and their information. Client data automatically populates documents when generating.',
    icon: Users,
    iconColor: 'from-pink-500 to-rose-600',
    targetPath: '/clients',
    tips: [
      'Link clients to matters for organized workflow',
      'Client portal lets clients view their documents',
      'Import clients from CSV for bulk setup',
    ],
  },
  {
    id: 'matters',
    title: 'Matter Tracking',
    description: 'Organize work by matters. Each matter can have multiple documents, a timeline, and be linked to one or more clients.',
    icon: Briefcase,
    iconColor: 'from-indigo-500 to-blue-600',
    targetPath: '/matters',
    tips: [
      'Create matter numbers for easy reference',
      'Track document status through workflow',
      'Generate multiple documents per matter',
    ],
  },
  {
    id: 'workflow',
    title: 'Document Workflow',
    description: 'Documents flow through stages: Draft → Review → Approve → Sign → Complete. Track status and send for e-signatures.',
    icon: Play,
    iconColor: 'from-teal-500 to-cyan-600',
    targetPath: '/documents',
    tips: [
      'Change status with one click',
      'Send documents for e-signature directly',
      'Download as PDF or Word',
    ],
  },
  {
    id: 'complete',
    title: 'You\'re Ready to Go!',
    description: 'You now know the basics. Start by creating your first template or importing existing ones. Our AI assistant is always here to help.',
    icon: CheckCircle,
    iconColor: 'from-green-500 to-emerald-600',
    position: 'center',
    action: {
      label: 'Create Your First Template',
      onClick: () => {},
    },
  },
];

export function OnboardingTour({ isOpen, onClose, onComplete }: OnboardingTourProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const step = TOUR_STEPS[currentStep];
  const progress = ((currentStep + 1) / TOUR_STEPS.length) * 100;

  const goToStep = useCallback((stepIndex: number) => {
    if (isAnimating) return;
    setIsAnimating(true);
    
    const targetStep = TOUR_STEPS[stepIndex];
    if (targetStep.targetPath && location.pathname !== targetStep.targetPath) {
      navigate(targetStep.targetPath);
    }
    
    setTimeout(() => {
      setCurrentStep(stepIndex);
      setIsAnimating(false);
    }, 300);
  }, [isAnimating, navigate, location.pathname]);

  const handleNext = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      goToStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      goToStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    // Save completion status to localStorage
    localStorage.setItem('onboarding_completed', 'true');
    localStorage.setItem('onboarding_completed_at', new Date().toISOString());
    onComplete();
  };

  const handleSkip = () => {
    localStorage.setItem('onboarding_skipped', 'true');
    onClose();
  };

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        e.preventDefault();
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrev();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        handleSkip();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentStep, isAnimating]);

  if (!isOpen) return null;

  const Icon = step.icon;

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key="backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 z-50"
      >
        {/* Progress Bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-primary-800">
          <motion.div
            className="h-full bg-gradient-to-r from-accent-400 to-accent-600"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>

        {/* Skip Button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 flex items-center gap-2 px-3 py-1.5 text-sm text-primary-400 hover:text-white transition-colors"
        >
          Skip Tour
          <X className="w-4 h-4" />
        </button>

        {/* Step Counter */}
        <div className="absolute top-4 left-4 text-sm text-primary-400">
          Step {currentStep + 1} of {TOUR_STEPS.length}
        </div>

        {/* Main Content */}
        <div className="h-full flex items-center justify-center p-6">
          <motion.div
            key={step.id}
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="w-full max-w-xl bg-primary-900 rounded-2xl border border-primary-700 shadow-2xl overflow-hidden"
          >
            {/* Icon Header */}
            <div className="relative h-32 bg-gradient-to-br from-primary-800 to-primary-900 flex items-center justify-center">
              <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${step.iconColor} flex items-center justify-center shadow-lg`}>
                <Icon className="w-10 h-10 text-white" />
              </div>
              
              {/* Decorative elements */}
              <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-4 left-4 w-2 h-2 rounded-full bg-accent-400/30" />
                <div className="absolute top-8 right-8 w-3 h-3 rounded-full bg-accent-400/20" />
                <div className="absolute bottom-6 left-12 w-2 h-2 rounded-full bg-accent-400/25" />
              </div>
            </div>

            {/* Content */}
            <div className="p-8">
              <h2 className="text-2xl font-display font-bold text-white mb-3">
                {step.title}
              </h2>
              <p className="text-primary-300 leading-relaxed mb-6">
                {step.description}
              </p>

              {/* Tips */}
              {step.tips && (
                <div className="space-y-2 mb-6">
                  {step.tips.map((tip, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <Sparkles className="w-4 h-4 text-accent-400 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-primary-400">{tip}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Action Button */}
              {step.action && currentStep === TOUR_STEPS.length - 1 && (
                <button
                  onClick={() => {
                    handleComplete();
                    navigate('/templates/new');
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 mb-4 bg-gradient-to-r from-accent-500 to-accent-600 text-white font-medium rounded-lg hover:from-accent-600 hover:to-accent-700 transition-all"
                >
                  {step.action.label}
                  <ArrowRight className="w-5 h-5" />
                </button>
              )}

              {/* Navigation */}
              <div className="flex items-center justify-between">
                <button
                  onClick={handlePrev}
                  disabled={currentStep === 0}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    currentStep === 0
                      ? 'text-primary-600 cursor-not-allowed'
                      : 'text-primary-300 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <ChevronLeft className="w-5 h-5" />
                  Previous
                </button>

                {/* Step Dots */}
                <div className="flex items-center gap-2">
                  {TOUR_STEPS.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => goToStep(idx)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        idx === currentStep
                          ? 'w-6 bg-accent-500'
                          : idx < currentStep
                          ? 'bg-accent-500/50'
                          : 'bg-primary-600'
                      }`}
                    />
                  ))}
                </div>

                <button
                  onClick={handleNext}
                  className="flex items-center gap-2 px-4 py-2 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors"
                >
                  {currentStep === TOUR_STEPS.length - 1 ? 'Finish' : 'Next'}
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Keyboard Hints */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-4 text-xs text-primary-500">
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-primary-800 rounded">←</kbd>
            <kbd className="px-1.5 py-0.5 bg-primary-800 rounded">→</kbd>
            navigate
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-primary-800 rounded">Enter</kbd>
            continue
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-primary-800 rounded">Esc</kbd>
            skip
          </span>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

// Hook to check if onboarding should be shown
export function useOnboarding() {
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    const completed = localStorage.getItem('onboarding_completed');
    const skipped = localStorage.getItem('onboarding_skipped');
    
    if (!completed && !skipped) {
      // Delay to let the app load first
      setTimeout(() => setShowOnboarding(true), 500);
    }
  }, []);

  const resetOnboarding = useCallback(() => {
    localStorage.removeItem('onboarding_completed');
    localStorage.removeItem('onboarding_completed_at');
    localStorage.removeItem('onboarding_skipped');
    setShowOnboarding(true);
  }, []);

  return {
    showOnboarding,
    setShowOnboarding,
    resetOnboarding,
  };
}
